import { useTacoConfigParameter } from '@coursera-web/taco-config-client';

import { TACO_NAMESPACE_PREVENT_ADDING_NEW_QUIZZES_EXCLUDED_COURSES } from 'bundles/assignment-submission/constants';
import AssignmentsExperiments from 'bundles/epic/clients/Assignments';
import localCourseId from 'bundles/phoenix/courseId';

type CourseExclusionsRolloutConfigType = {
  alwaysExcludeCourses: Array<string>;
  alwaysExcludePartnerIds: Array<string>;
};

export const shouldPreventAddingNewQuizzes = (
  courseId: string,
  coursesExcludedFromQuizDeprecation: string[] = []
): boolean => {
  if (coursesExcludedFromQuizDeprecation.includes(courseId)) {
    return false;
  }

  // Epic was 100% rolled out, so feature is always enabled except for excluded courses
  return true;
};

export const usePreventAddingNewQuizzesExcludedCourses = (): {
  coursesExcludedFromQuizDeprecation: string[];
  loading: boolean;
  error?: string;
} => {
  const {
    data: coursesExcludedFromQuizDeprecation,
    loading,
    error,
  } = useTacoConfigParameter<string[]>(
    TACO_NAMESPACE_PREVENT_ADDING_NEW_QUIZZES_EXCLUDED_COURSES,
    'alwaysExcludeCourses'
  );

  return {
    coursesExcludedFromQuizDeprecation: coursesExcludedFromQuizDeprecation || [],
    loading,
    error,
  };
};

export const isReviewSubmissionScreenEnabledForAutoGradedQuestions = () => {
  return AssignmentsExperiments.get('enableReviewSubmissionScreenForAutoGradedQuestions');
};

export const isRetryAssignmentFromFeedbackPageEnabled = () => {
  return AssignmentsExperiments.get('enableRetryAsmntFromFeedbackPage');
};

export const isNextGenAssignmentsQuestionByQuestionEnabled = ({
  courseId = undefined,
  partnerId = undefined,
}: {
  courseId?: string;
  partnerId?: string;
}) => {
  const rolloutConfig: CourseExclusionsRolloutConfigType = AssignmentsExperiments.preview(
    'nextGenAssignmentsQuestionByQuestionRolloutConfig'
  );

  const { alwaysExcludeCourses = [], alwaysExcludePartnerIds = [] } = rolloutConfig || {};
  const currentCourseId = courseId ?? localCourseId;

  if (currentCourseId && alwaysExcludeCourses.includes(currentCourseId)) {
    return false;
  }

  if (partnerId && alwaysExcludePartnerIds.includes(partnerId)) {
    return false;
  }

  return AssignmentsExperiments.get('enableNextGenAssignmentsQuestionByQuestion_90-10');
};

// TODO: delete function after AA data collection is complete
export const isNextGenAssignmentsQuestionByQuestionExclusionAATestEnabled = ({
  courseId = undefined,
  partnerId = undefined,
}: {
  courseId?: string;
  partnerId?: string;
}) => {
  const rolloutConfig: CourseExclusionsRolloutConfigType = AssignmentsExperiments.preview(
    'nextGenAssignmentsQuestionByQuestionRolloutConfig'
  );

  const { alwaysExcludeCourses = [], alwaysExcludePartnerIds = [] } = rolloutConfig || {};
  const currentCourseId = courseId ?? localCourseId;

  if (currentCourseId && alwaysExcludeCourses.includes(currentCourseId)) {
    return false;
  }

  if (partnerId && alwaysExcludePartnerIds.includes(partnerId)) {
    return false;
  }

  return AssignmentsExperiments.get('enableNextGenAssignmentsPhase1AAExclusionTest');
};

type GeneratedCodingPracticeTest1Config = {
  itemIncludeList: Record<
    // keys: courseId
    string,
    // values: itemIds
    string[]
  >;
};

export const isGeneratedCodingPracticeTest1Enabled = (courseId?: string, itemId?: string): boolean => {
  // https://tools.coursera.org/epic/experiment/GJDIE0V_EfCdgg4J2FZTGw
  const isEnabled: boolean = !!AssignmentsExperiments.get('enableGeneratedCodingPracticeTest1');
  // https://tools.coursera.org/epic/experiment/dvW1_0WAEfCVowr_5BhkRQ
  const generatedCodingPracticeTest1Config: GeneratedCodingPracticeTest1Config = AssignmentsExperiments.get(
    'generatedCodingPracticeTest1Config'
  );
  const isItemIncluded: boolean =
    !!courseId && !!itemId && !!generatedCodingPracticeTest1Config.itemIncludeList[courseId]?.includes(itemId);
  return isEnabled && isItemIncluded;
};

export const isMasteryLevelsEnabled = () => {
  return AssignmentsExperiments.get('enableMasteryLevels');
};
